import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, FileDown, Search, Inbox, Loader2, ExternalLink, Factory } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const STATUSES = ["New Inquiry", "Technical Review", "Quote Sent", "Sample Production", "Completed"];
const STATUS_CLASS = {
  "New Inquiry": "status-new",
  "Technical Review": "status-review",
  "Quote Sent": "status-quote",
  "Sample Production": "status-sample",
  "Completed": "status-completed",
};

const StatusPill = ({ value }) => (
  <span data-testid={`status-pill-${value}`} className={`inline-block px-3 py-1 text-[10px] uppercase tracking-[0.18em] border ${STATUS_CLASS[value] || "status-new"}`}>{value}</span>
);

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) + " · " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const StatCard = ({ label, value, accent, testId }) => (
  <div data-testid={testId} className="border border-[#0a3a47] bg-[#0a3845] p-5">
    <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold">{label}</div>
    <div className={`font-display text-3xl font-bold mt-2 ${accent || "text-slate-100"}`}>{value}</div>
  </div>
);

export default function AdminDashboard() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate("/admin/login");
  }, [authLoading, user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rfqs");
      setRfqs(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load RFQs");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user) load(); }, [user]);

  const filtered = useMemo(() => {
    return rfqs.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          r.company_name.toLowerCase().includes(q) ||
          r.contact_person.toLowerCase().includes(q) ||
          r.contact_email.toLowerCase().includes(q) ||
          (r.polymer_type || "").toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [rfqs, statusFilter, search]);

  const stats = useMemo(() => {
    const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: 0 }), {});
    rfqs.forEach((r) => { counts[r.status] = (counts[r.status] || 0) + 1; });
    return { total: rfqs.length, ...counts };
  }, [rfqs]);

  const changeStatus = async (rfqId, status) => {
    try {
      const { data } = await api.patch(`/rfqs/${rfqId}/status`, { status });
      setRfqs((rs) => rs.map((r) => (r.id === rfqId ? data : r)));
      if (selected && selected.id === rfqId) setSelected(data);
      toast.success(`Status updated to ${status}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const addNote = async () => {
    if (!selected || !newNote.trim()) return;
    setSavingNote(true);
    try {
      const { data } = await api.post(`/rfqs/${selected.id}/notes`, { note: newNote.trim() });
      setSelected(data);
      setRfqs((rs) => rs.map((r) => (r.id === selected.id ? data : r)));
      setNewNote("");
      toast.success("Note added");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to add note");
    } finally { setSavingNote(false); }
  };

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#062632]">
        <Loader2 className="w-6 h-6 text-[#0dd3ce] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#062632] text-slate-200">
      <header className="border-b border-[#0a3a47] bg-[#03161D] sticky top-0 z-40">
        <div className="px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/brand/a3-sagar-logo-transparent.png" alt="A3 Sagar" className="h-11 w-auto" style={{ filter: "brightness(2.4) saturate(1.1)" }} />
            <div>
              <div className="font-display font-bold text-slate-50 text-lg tracking-tight">A3 SAGAR · Control Room</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Lead Management</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-slate-500 hidden sm:inline">{user.email}</span>
            <button data-testid="admin-logout-btn" onClick={doLogout} className="inline-flex items-center gap-2 border border-[#0e4555] px-4 py-2 text-xs uppercase tracking-wider text-slate-300 hover:bg-slate-800">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="px-6 md:px-10 py-10 max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#0dd3ce] font-semibold">Dashboard</div>
            <h1 data-testid="admin-title" className="font-display text-3xl sm:text-4xl font-bold text-slate-100 mt-1 tracking-tight">Incoming RFQs</h1>
            <p className="text-sm text-slate-500 mt-2">Track every B2B inquiry from new submission through completed delivery.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8">
          <StatCard testId="stat-total" label="Total" value={stats.total} accent="text-[#0dd3ce]" />
          <StatCard testId="stat-new" label="New" value={stats["New Inquiry"]} />
          <StatCard testId="stat-review" label="Review" value={stats["Technical Review"]} />
          <StatCard testId="stat-quote" label="Quote Sent" value={stats["Quote Sent"]} />
          <StatCard testId="stat-sample" label="Sample" value={stats["Sample Production"]} />
          <StatCard testId="stat-completed" label="Completed" value={stats["Completed"]} />
        </div>

        <div className="border border-[#0a3a47] bg-[#0a3845]">
          <div className="p-4 flex flex-col sm:flex-row gap-3 border-b border-[#0a3a47]">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input data-testid="admin-search-input" placeholder="Search company, contact, polymer…" value={search} onChange={(e) => setSearch(e.target.value)} className="bg-[#03161D] border-[#0e4555] text-slate-100 pl-9 rounded-none h-11" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="admin-status-filter" className="w-full sm:w-56 bg-[#03161D] border-[#0e4555] text-slate-100 rounded-none h-11"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0a3845] border-[#0e4555] text-slate-100">
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="p-20 grid place-items-center">
              <Loader2 className="w-6 h-6 text-[#0dd3ce] animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div data-testid="empty-state" className="p-16 text-center">
              <Inbox className="w-10 h-10 text-slate-600 mx-auto mb-4" />
              <div className="text-slate-300 font-semibold">No RFQs yet</div>
              <div className="text-xs text-slate-500 mt-1">Submissions from the public RFQ form will land here.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#0a3a47] hover:bg-transparent">
                    <TableHead className="text-slate-300 uppercase text-[10px] tracking-widest">Date</TableHead>
                    <TableHead className="text-slate-300 uppercase text-[10px] tracking-widest">Company</TableHead>
                    <TableHead className="text-slate-300 uppercase text-[10px] tracking-widest">Polymer</TableHead>
                    <TableHead className="text-slate-300 uppercase text-[10px] tracking-widest">Status</TableHead>
                    <TableHead className="text-slate-300 uppercase text-[10px] tracking-widest text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => (
                    <TableRow key={r.id} data-testid={`rfq-row-${r.id}`} className="border-[#0a3a47] hover:bg-[#0e4555]/60">
                      <TableCell className="text-slate-400 text-xs">{fmtDate(r.created_at)}</TableCell>
                      <TableCell>
                        <div className="text-slate-100 font-semibold">{r.company_name}</div>
                        <div className="text-xs text-slate-500">{r.contact_person} · {r.contact_email}</div>
                      </TableCell>
                      <TableCell className="text-slate-300 text-sm">{r.polymer_type}</TableCell>
                      <TableCell>
                        <Select value={r.status} onValueChange={(v) => changeStatus(r.id, v)}>
                          <SelectTrigger data-testid={`status-select-${r.id}`} className="w-44 bg-[#03161D] border-[#0e4555] text-slate-100 rounded-none h-9 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#0a3845] border-[#0e4555] text-slate-100">
                            {STATUSES.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <button data-testid={`view-rfq-${r.id}`} onClick={() => setSelected(r)} className="inline-flex items-center gap-1 text-[#0dd3ce] hover:text-[#5ce7e3] text-xs uppercase tracking-wider">
                          Open <ExternalLink className="w-3 h-3" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </main>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent data-testid="rfq-detail-sheet" side="right" className="bg-[#03161D] border-[#0a3a47] text-slate-200 w-full sm:max-w-xl overflow-y-auto">
          {selected && (
            <>
              <SheetHeader>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#0dd3ce] font-semibold">RFQ Detail</div>
                <SheetTitle className="font-display text-2xl text-slate-100 mt-1">{selected.company_name}</SheetTitle>
                <SheetDescription className="text-slate-500">Submitted {fmtDate(selected.created_at)}</SheetDescription>
              </SheetHeader>

              <div className="mt-6">
                <StatusPill value={selected.status} />
              </div>

              <div className="mt-6 border border-[#0a3a47] bg-[#0a3845] p-5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3 font-semibold">Inquiry</div>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  {[
                    ["GSTIN", selected.gstin || "—"],
                    ["Contact", selected.contact_person],
                    ["Email", selected.contact_email],
                    ["Phone", selected.contact_phone || "—"],
                    ["Polymer", selected.polymer_type],
                    ["Volume", selected.order_volume],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <dt className="text-[10px] uppercase tracking-widest text-slate-500">{k}</dt>
                      <dd className="text-slate-100 mt-0.5 break-words">{v}</dd>
                    </div>
                  ))}
                </dl>
                {selected.additional_notes && (
                  <div className="mt-4 pt-4 border-t border-[#0a3a47]">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Additional Notes</div>
                    <div className="text-sm text-slate-200 mt-1 whitespace-pre-wrap">{selected.additional_notes}</div>
                  </div>
                )}
              </div>

              {selected.attachment && (
                <div className="mt-4 border border-[#0a3a47] bg-[#0a3845] p-5">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3 font-semibold">Attachment</div>
                  <a
                    data-testid="download-attachment-btn"
                    href={`${API}/files/${selected.attachment.storage_path}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-[#0dd3ce] hover:text-[#5ce7e3] text-sm"
                  >
                    <FileDown className="w-4 h-4" /> {selected.attachment.original_filename}
                  </a>
                  <div className="text-[11px] text-slate-500 mt-1">{(selected.attachment.size / 1024).toFixed(1)} KB · {selected.attachment.content_type}</div>
                </div>
              )}

              <div className="mt-4 border border-[#0a3a47] bg-[#0a3845] p-5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3 font-semibold">Pipeline Status</div>
                <Select value={selected.status} onValueChange={(v) => changeStatus(selected.id, v)}>
                  <SelectTrigger data-testid="detail-status-select" className="bg-[#03161D] border-[#0e4555] text-slate-100 rounded-none h-11"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0a3845] border-[#0e4555] text-slate-100">
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-4 border border-[#0a3a47] bg-[#0a3845] p-5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3 font-semibold">Internal Log</div>
                <Textarea
                  data-testid="note-input"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="e.g., Hussain spoke to client; pricing needs adjustment for high volume."
                  className="bg-[#03161D] border-[#0e4555] text-slate-100 rounded-none min-h-[90px]"
                />
                <button
                  data-testid="add-note-btn"
                  onClick={addNote}
                  disabled={savingNote || !newNote.trim()}
                  className="mt-3 bg-[#0dd3ce] text-[#03161D] font-semibold px-5 py-2 text-xs uppercase tracking-wider hover:bg-[#5ce7e3] disabled:opacity-50"
                >
                  {savingNote ? "Saving…" : "Add Log Note"}
                </button>

                <div className="mt-5 space-y-3">
                  {(selected.notes || []).length === 0 && (
                    <div className="text-xs text-slate-500">No notes yet.</div>
                  )}
                  {(selected.notes || []).slice().reverse().map((n) => (
                    <div key={n.id} data-testid={`note-${n.id}`} className="border-l-2 border-[#0dd3ce]/40 pl-3 py-1">
                      <div className="text-sm text-slate-200 whitespace-pre-wrap">{n.note}</div>
                      <div className="text-[10px] text-slate-500 mt-1 uppercase tracking-widest">{n.author} · {fmtDate(n.created_at)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
